import { useState } from 'react';
import { CheckCircle2, Trash2 } from 'lucide-react';
import { StatusActions, ConvertDropdown } from '../quick-actions';

export default function InboxProcessor({
  inboxItems = [],
  onAddToToday,
  onMoveToWaiting,
  onMoveToSomeday,
  onSchedule,
  onDelete,
  onCreateProject,
  onAddToOneOnOne,
  onAddAsGoal,
  onAddAsHabit,
  processedCount = 0
}) {
  const [processingId, setProcessingId] = useState(null);

  const handleAction = async (action, taskId, ...args) => {
    setProcessingId(taskId);
    try {
      await action(taskId, ...args);
    } finally {
      setProcessingId(null);
    }
  };

  if (inboxItems.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <CheckCircle2 size={64} className="text-success mb-4 opacity-50" />
        <h3 className="text-xl font-semibold text-text-primary mb-2">
          Inbox Zero!
        </h3>
        <p className="text-sm text-text-tertiary max-w-xs">
          All inbox items have been processed. You're ready for the next step.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Items list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {inboxItems.map((item) => (
          <div
            key={item.id}
            className={`
              p-4 bg-surface-card rounded-xl border border-border
              transition-all duration-200
              ${processingId === item.id ? 'opacity-50 scale-98' : ''}
            `}
          >
            {/* Task title and notes */}
            <div className="mb-3">
              <h4 className="text-sm font-medium text-text-primary mb-1">
                {item.title}
              </h4>
              {item.notes && (
                <p className="text-xs text-text-tertiary line-clamp-2">
                  {item.notes}
                </p>
              )}
              {item.source && item.source !== 'manual' && (
                <span className="inline-block mt-1 text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded">
                  From {item.source}
                </span>
              )}
            </div>

            {/* Actions: Status on left, Convert + Delete on right */}
            <div className="flex items-center justify-between gap-2">
              {/* Left side: Status actions (monochrome with 1:1) */}
              <div className="flex items-center gap-1 flex-wrap">
                <StatusActions
                  task={item}
                  onAddToToday={() => handleAction(onAddToToday, item.id)}
                  onSchedule={(taskId, date) => handleAction(onSchedule, item.id, date)}
                  onMoveToWaiting={(taskId, waitingFor, delegatedTo, followUpDate) =>
                    handleAction(onMoveToWaiting, item.id, waitingFor, delegatedTo, followUpDate)
                  }
                  onMoveToSomeday={() => handleAction(onMoveToSomeday, item.id)}
                  onAddToOneOnOne={() => onAddToOneOnOne?.(item)}
                  disabled={processingId === item.id}
                  showDelete={false}
                  showDone={false}
                  showOneOnOne={true}
                  monochrome={true}
                />
              </div>

              {/* Right side: Convert dropdown + Delete */}
              <div className="flex items-center gap-1.5">
                <ConvertDropdown
                  onCreateProject={() => onCreateProject?.(item)}
                  onAddAsGoal={() => onAddAsGoal?.(item)}
                  onAddAsHabit={() => onAddAsHabit?.(item)}
                  disabled={processingId === item.id}
                />
                <button
                  onClick={() => handleAction(onDelete, item.id)}
                  disabled={processingId === item.id}
                  className="p-1 rounded-md border-none cursor-pointer transition-all duration-200 text-text-muted bg-transparent hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
